import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../../api/client'
import type { MaintenanceWindow, PatchCatalog, PatchFleet, PatchJobDetail, PatchJobPlan } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { DataTable } from '../DataTable'
import { ErrorBanner, Spinner } from '../common'
import {
  KevChip, PriorityChip, ReadinessChips, SeverityChip, chipClass, kindLabel, patchKindLabel, platformLabel,
  reasonHint, reasonLabel, useIsTenantAdmin,
} from './labels'

/** What a caller already knows when it opens the wizard (a patch, some machines). */
export interface WizardPrefill {
  kind?: 'security_all' | 'all_updates' | 'selected' | 'reboot' | 'third_party'
  patchKeys?: string[]
  ciIds?: string[]
  tag?: string
  name?: string
}

type Step = 'what' | 'where' | 'when' | 'review'
const STEPS: Step[] = ['what', 'where', 'when', 'review']

/**
 * «Nuovo intervento»: four questions, one per step, each with the answer most
 * people want already chosen. What (the security updates, everything, the
 * patches you pick, or a reboot), where (machines or a tag), when (now, a
 * maintenance window or a date) with the reboot policy, and a last page that
 * says machine by machine what will happen — and why a machine will not —
 * before anything is created.
 */
export function NewJobWizard({ prefill, fleet: fleetProp, catalog: catalogProp, onClose, onCreated }: {
  prefill?: WizardPrefill
  fleet: PatchFleet | null
  catalog: PatchCatalog | null
  onClose: () => void
  onCreated: (job: PatchJobDetail) => void
}) {
  const admin = useIsTenantAdmin()
  const windows = useAsync<MaintenanceWindow[]>(() => api.patchWindows(), [])
  // Opened from a machine's page the lists are not loaded yet: load them here.
  const fleetState = useAsync<PatchFleet | null>(() => (fleetProp ? Promise.resolve(fleetProp) : api.patchFleet()), [])
  const catalogState = useAsync<PatchCatalog | null>(
    () => (catalogProp ? Promise.resolve(catalogProp) : api.patchCatalog()), [])
  const fleet = fleetProp ?? fleetState.data
  const catalog = catalogProp ?? catalogState.data
  const firstStep: Step = prefill?.kind && (prefill.ciIds?.length || prefill.tag) ? 'when'
    : prefill?.kind ? 'where' : 'what'
  const [step, setStep] = useState<Step>(firstStep)
  const [kind, setKind] = useState<NonNullable<WizardPrefill['kind']>>(prefill?.kind ?? 'security_all')
  const [patchKeys, setPatchKeys] = useState<Set<string>>(new Set(prefill?.patchKeys ?? []))
  const [targetMode, setTargetMode] = useState<'machines' | 'tag'>(prefill?.tag ? 'tag' : 'machines')
  const [ciIds, setCiIds] = useState<Set<string>>(new Set(prefill?.ciIds ?? []))
  // Machines chosen before opening the wizard (the machine list's selection):
  // «Where» shows just them, already ticked, instead of the whole fleet where
  // they would sit on another page.
  const [preChosen] = useState<Set<string>>(() => new Set(prefill?.ciIds ?? []))
  const [visible, setVisible] = useState<Set<string> | null>(() => (preChosen.size ? preChosen : null))
  const [tag, setTag] = useState(prefill?.tag ?? '')
  const [schedule, setSchedule] = useState<'now' | 'window' | 'at'>('now')
  const [windowId, setWindowId] = useState('')
  const [at, setAt] = useState('')
  const [allowReboot, setAllowReboot] = useState(false)
  const [delay, setDelay] = useState(10)
  const [name, setName] = useState(prefill?.name ?? '')
  const [plan, setPlan] = useState<PatchJobPlan | null>(null)
  const [planning, setPlanning] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const envKey = fleet?.environment_key ?? 'ambiente'
  const envValues = useMemo(
    () => [...new Set((fleet?.hosts ?? []).map((h) => h.environment).filter(Boolean))].sort(),
    [fleet],
  )
  // With machines already chosen, only the patches those machines miss (when
  // the catalogue lists all of a patch's machines; beyond 500 it keeps it).
  const installable = (catalog?.patches ?? []).filter((p) => p.installable && (
    !preChosen.size || p.hosts.length < p.host_count || p.hosts.some((h) => preChosen.has(h.ci_id))))
  const hosts = fleet?.hosts ?? []
  const pickerRows = useMemo(
    () => (visible ? hosts.filter((h) => visible.has(h.ci_id))
      : [...hosts].sort((a, b) => Number(preChosen.has(b.ci_id)) - Number(preChosen.has(a.ci_id)))),
    [hosts, visible, preChosen],
  )

  const targets = targetMode === 'tag'
    ? { tag: tag.trim(), ci_ids: [] as string[] }
    : { tag: '', ci_ids: [...ciIds] }
  const spec = kind === 'selected' ? { patch_keys: [...patchKeys] } : undefined

  // The review page asks the Control Plane what would happen, machine by machine.
  useEffect(() => {
    if (step !== 'review') return
    setPlanning(true)
    setError(null)
    api.patchJobPlan({ kind, spec, ...targets })
      .then(setPlan)
      .catch((e) => { setPlan(null); setError(e) })
      .finally(() => setPlanning(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const canNext = step === 'what' ? (kind !== 'selected' || patchKeys.size > 0)
    : step === 'where' ? (targetMode === 'tag' ? Boolean(tag.trim()) : ciIds.size > 0)
      : step === 'when' ? (schedule !== 'window' || Boolean(windowId)) && (schedule !== 'at' || Boolean(at))
        : Boolean(plan && plan.will_run + plan.blocked > 0)

  async function create(approve: boolean) {
    setSaving(true)
    setError(null)
    try {
      let job = await api.patchJobCreate({
        name: name.trim() || undefined, kind, spec, ...targets, schedule,
        window_id: schedule === 'window' ? windowId : undefined,
        scheduled_at: schedule === 'at' && at ? new Date(at).toISOString() : undefined,
        allow_reboot: allowReboot, reboot_delay_min: delay,
      })
      if (approve) job = await api.patchJobApprove(job.id)
      onCreated(job)
    } catch (e) {
      setError(e)
    } finally {
      setSaving(false)
    }
  }

  const stepLabel: Record<Step, string> = {
    what: t('Cosa'), where: t('Dove'), when: t('Quando'), review: t('Riepilogo'),
  }
  const idx = STEPS.indexOf(step)

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Nuovo intervento')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello patch-wizard">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Nuovo intervento')}</h3>
            <ol className="patch-steps">
              {STEPS.map((s, i) => (
                <li key={s} className={i === idx ? 'patch-steps__on' : i < idx ? 'patch-steps__done' : ''}>
                  <button type="button" className="link" disabled={i > idx} onClick={() => setStep(s)}>
                    {i + 1}. {stepLabel[s]}
                  </button>
                </li>
              ))}
            </ol>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
          </div>
        </div>
        <ErrorBanner error={error} />

        {step === 'what' && (
          <div className="patch-wizard__body">
            <h4>{t('Cosa installare?')}</h4>
            {preChosen.size > 0 && (
              <p className="field__hint">
                {t('Sulle {n} macchine che hai scelto: le ritrovi già spuntate nel passo «Dove».', { n: preChosen.size })}
              </p>
            )}
            <div className="patch-options">
              <Option checked={kind === 'security_all'} onPick={() => setKind('security_all')}
                title={t('Aggiornamenti di sicurezza mancanti')}
                text={t('Su Windows gli aggiornamenti di sicurezza da Windows Update o dal WSUS dell’azienda; su Linux i pacchetti con correzioni di sicurezza (apt, dnf, yum, zypper, apk). È la scelta di ogni mese.')} />
              <Option checked={kind === 'all_updates'} onPick={() => setKind('all_updates')}
                title={t('Tutti gli aggiornamenti disponibili')}
                text={t('Anche quelli non di sicurezza (correzioni, nuove versioni minori). Da preferire in collaudo prima della produzione.')} />
              <Option checked={kind === 'third_party'} onPick={() => setKind('third_party')}
                title={t('Programmi di terze parti')}
                text={t('Su Windows tutti i programmi che hanno una versione nuova — Chrome, Firefox, Acrobat Reader, 7-Zip, Zoom… — con winget o Chocolatey della macchina, e Chrome ed Edge anche con il loro aggiornatore.')} />
              <Option checked={kind === 'selected'} onPick={() => setKind('selected')}
                title={t('Patch scelte')}
                text={t('Solo le KB, i pacchetti o i programmi che scegli qui sotto: su ogni macchina si installa solo quello che le manca.')} />
              <Option checked={kind === 'reboot'} onPick={() => setKind('reboot')}
                title={t('Solo riavvio')}
                text={t('Riavvia le macchine che aspettano un riavvio per completare un’installazione, con preavviso agli utenti collegati.')} />
            </div>
            {kind === 'selected' && (
              installable.length === 0 ? (
                <p className="text-muted">{t('Nessuna KB, pacchetto o programma mancante da scegliere.')}</p>
              ) : (
                <DataTable
                  rows={installable}
                  rowKey={(r) => r.key}
                  conteggio={{ nome: t('patch') }}
                  paginazione={{ predefinita: 10 }}
                  selezione={{ chiavi: patchKeys, onCambia: setPatchKeys, id: (r) => r.key }}
                  columns={[
                    { key: 'p', header: t('Priorità'), value: (r) => r.priority, render: (r) => <PriorityChip priority={r.priority_label} />, filter: false },
                    {
                      key: 'name', header: t('Patch'), value: (r) => `${r.name} ${r.title}`,
                      render: (r) => <><span className="mono">{r.name}</span> {r.exploited && <KevChip />}<div className="text-muted patch-sub">{patchKindLabel(r.kind)}</div></>,
                    },
                    { key: 's', header: t('Gravità'), value: (r) => r.severity, render: (r) => <SeverityChip severity={r.severity} />, filter: 'select' },
                    { key: 'h', header: t('Macchine'), value: (r) => r.host_count },
                  ]}
                />
              )
            )}
          </div>
        )}

        {step === 'where' && (
          <div className="patch-wizard__body">
            <h4>{t('Su quali macchine?')}</h4>
            <div className="segmented" role="group">
              <button type="button" className={targetMode === 'machines' ? 'segmented__on' : ''} onClick={() => setTargetMode('machines')}>
                {t('Scelgo le macchine')}
              </button>
              <button type="button" className={targetMode === 'tag' ? 'segmented__on' : ''} onClick={() => setTargetMode('tag')}>
                {t('Per tag (es. ambiente)')}
              </button>
            </div>
            {targetMode === 'tag' ? (
              <div className="field">
                <label htmlFor="patch-tag">{t('Tag')}</label>
                <input id="patch-tag" value={tag} onChange={(e) => setTag(e.target.value)} placeholder={t('es. {k}:collaudo', { k: envKey })} />
                {envValues.length > 0 && (
                  <div className="patch-tagpick">
                    {envValues.map((v) => (
                      <button key={v} type="button" className="secondary" onClick={() => setTag(`${envKey}:${v}`)}>
                        {envKey}:{v} ({(fleet?.hosts ?? []).filter((h) => h.environment === v).length})
                      </button>
                    ))}
                  </div>
                )}
                <p className="field__hint">{t('Tutte le macchine con questo tag al momento della creazione.')}</p>
              </div>
            ) : (
              <>
                <p className="field__hint">
                  {visible
                    ? t('Le macchine che hai scelto ({n}): togli la spunta per escluderne una.', { n: ciIds.size })
                    : t('{n} scelte. Le macchine non pronte si possono scegliere: il riepilogo dirà cosa manca.', { n: ciIds.size })}
                  {' '}
                  {visible ? (
                    <button type="button" className="link" onClick={() => setVisible(null)}>{t('Scegli fra tutte le macchine')}</button>
                  ) : ciIds.size > 0 && (
                    <button type="button" className="link" onClick={() => setVisible(new Set(ciIds))}>{t('Mostra solo le scelte')}</button>
                  )}
                </p>
                <DataTable
                  rows={pickerRows}
                  rowKey={(r) => r.ci_id}
                  conteggio={{ nome: t('macchine') }}
                  paginazione={{ predefinita: 10 }}
                  selezione={{ chiavi: ciIds, onCambia: setCiIds, id: (r) => r.ci_id }}
                  columns={[
                    { key: 'name', header: t('Macchina'), value: (r) => r.name },
                    { key: 'platform', header: t('Sistema'), value: (r) => platformLabel(r.platform), filter: 'select' },
                    { key: 'env', header: t('Ambiente'), value: (r) => r.environment, filter: 'select' },
                    { key: 'missing', header: t('Patch mancanti'), value: (r) => r.missing },
                    {
                      key: 'ready', header: t('Aggiornabile'), value: (r) => r.reasons.join(' '),
                      render: (r) => <ReadinessChips reasons={r.reasons} missingTags={r.missing_tags} />,
                    },
                  ]}
                />
              </>
            )}
          </div>
        )}

        {step === 'when' && (
          <div className="patch-wizard__body">
            <h4>{t('Quando?')}</h4>
            <div className="patch-options">
              <Option checked={schedule === 'now'} onPick={() => setSchedule('now')}
                title={t('Subito dopo l’approvazione')}
                text={t('Ogni macchina parte al primo contatto dell’agente (circa un minuto); quelle spente partono quando tornano.')} />
              <Option checked={schedule === 'window'} onPick={() => setSchedule('window')}
                title={t('In una finestra di manutenzione')}
                text={t('Parte solo dentro la finestra (giorni e orari), ogni volta che si apre finché non ha finito.')}>
                {schedule === 'window' && (
                  (windows.data ?? []).length === 0 ? (
                    <p className="text-muted">{t('Nessuna finestra: creala nella sezione «Finestre».')}</p>
                  ) : (
                    <select value={windowId} onChange={(e) => setWindowId(e.target.value)} aria-label={t('Finestra')}>
                      <option value="">{t('scegli la finestra…')}</option>
                      {(windows.data ?? []).filter((w) => w.enabled).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  )
                )}
              </Option>
              <Option checked={schedule === 'at'} onPick={() => setSchedule('at')}
                title={t('In una data e ora')} text={t('Una volta sola, dall’ora indicata.')}>
                {schedule === 'at' && (
                  <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} aria-label={t('Data e ora')} />
                )}
              </Option>
            </div>
            <h4>{t('Riavvio')}</h4>
            <div className="patch-options">
              <Option checked={!allowReboot} onPick={() => setAllowReboot(false)}
                title={t('Non riavviare')}
                text={t('Se una macchina deve riavviarsi resta «Da riavviare»; il riavvio lo fai tu, o con un intervento «Solo riavvio».')} />
              <Option checked={allowReboot} onPick={() => setAllowReboot(true)}
                title={t('Riavvia se serve')}
                text={t('Solo le macchine che lo chiedono, con un messaggio agli utenti collegati e un preavviso.')}>
                {allowReboot && (
                  <label className="patch-inline">
                    {t('Preavviso')}{' '}
                    <input type="number" min={1} max={240} value={delay} onChange={(e) => setDelay(Number(e.target.value) || 10)} style={{ width: 80 }} />
                    {' '}{t('minuti')}
                  </label>
                )}
              </Option>
            </div>
            <div className="field">
              <label htmlFor="patch-name">{t('Nome (facoltativo)')}</label>
              <input id="patch-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t('es. Aggiornamenti di ottobre — collaudo')} />
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="patch-wizard__body">
            <h4>{t('Riepilogo')}</h4>
            <p>
              <strong>{kindLabel(kind)}</strong>
              {kind === 'selected' && <span className="mono"> — {[...patchKeys].map((k) => k.split(':').slice(1).join(':')).join(', ')}</span>}
            </p>
            {planning || !plan ? (planning ? <Spinner label={t('Calcolo cosa succederà su ogni macchina…')} /> : null) : (
              <>
                <div className="stats">
                  <div className="stat stat--ok"><div className="stat__value">{plan.will_run}</div><div className="stat__label">{t('partiranno')}</div></div>
                  <div className={`stat${plan.blocked ? ' stat--error' : ''}`}><div className="stat__value">{plan.blocked}</div><div className="stat__label">{t('bloccate finché non si sistema')}</div></div>
                  <div className="stat"><div className="stat__value">{plan.skipped}</div><div className="stat__label">{t('escluse')}</div></div>
                </div>
                <DataTable
                  rows={plan.machines}
                  rowKey={(r) => r.ci_id}
                  paginazione={{ predefinita: 10 }}
                  columns={[
                    { key: 'name', header: t('Macchina'), value: (r) => r.name },
                    { key: 'platform', header: t('Sistema'), value: (r) => platformLabel(r.platform), filter: 'select' },
                    {
                      key: 'what', header: t('Cosa farà'), value: (r) => r.skip ?? r.task_kind ?? '',
                      render: (r) => r.skip
                        ? <span className={chipClass('muted')} title={reasonHint(r.skip)}>{reasonLabel(r.skip)}</span>
                        : <span>{(r.tasks ?? []).map((x) => kindLabel(x.kind) + taskSpecText(x.spec)).join(' + ')}</span>,
                    },
                    {
                      key: 'ready', header: t('Stato'), value: (r) => r.reasons.join(' '),
                      render: (r) => (r.skip ? null : <ReadinessChips reasons={r.reasons} />),
                    },
                  ]}
                />
                <p className="field__hint">
                  {t('Le bloccate restano nell’intervento: sistemato il motivo (agente, tag), «Riprova le bloccate» le fa partire. Agente spento o da aggiornare non blocca: parte appena può.')}
                </p>
              </>
            )}
          </div>
        )}

        <div className="patch-wizard__foot">
          {idx > 0 && <button type="button" className="secondary" onClick={() => setStep(STEPS[idx - 1])}>{t('Indietro')}</button>}
          {step !== 'review' ? (
            <button type="button" disabled={!canNext} onClick={() => setStep(STEPS[idx + 1])}>{t('Avanti')}</button>
          ) : (
            <>
              <button type="button" className={admin ? 'secondary' : ''} disabled={!canNext || saving} onClick={() => create(false)}
                title={t('Crea l’intervento da approvare: niente parte prima.')}>
                {t('Crea da approvare')}
              </button>
              {admin && (
                <button type="button" disabled={!canNext || saving} onClick={() => create(true)}>
                  {t('Crea e approva')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function taskSpecText(spec?: Record<string, unknown>): string {
  if (!spec) return ''
  const kbs = (spec.kbs as string[] | undefined) ?? []
  const pkgs = ((spec.packages as Array<{ name: string }> | undefined) ?? []).map((p) => p.name)
  const programs = (spec.products as string[] | undefined) ?? []
  const all = [...kbs, ...pkgs, ...programs]
  return all.length ? `: ${all.join(', ')}` : ''
}

function Option({ checked, onPick, title, text, children }: {
  checked: boolean; onPick: () => void; title: string; text: string; children?: ReactNode
}) {
  return (
    <div className={`patch-option${checked ? ' patch-option--on' : ''}`}>
      <label>
        <input type="radio" checked={checked} onChange={onPick} />
        <span>
          <strong>{title}</strong>
          <span className="patch-option__text">{text}</span>
        </span>
      </label>
      {children && <div className="patch-option__extra">{children}</div>}
    </div>
  )
}
